import {
  CustomerBillingProfileWriteResponseSchema, CustomerContactWriteResponseSchema,
  CustomerListResponseSchema, CustomerSummarySchema, CustomerWriteResponseSchema,
  ProjectCreationOptionsResponseSchema,
  type CreateCustomerContactRequest, type CustomerBillingProfileWriteResponse,
  type CustomerContactWriteResponse, type CustomerListResponse, type CustomerSummary,
  type CustomerType, type CustomerWriteResponse, type ProjectCreationOptionsResponse,
  type SaveCustomerBillingProfileRequest, type UpdateCustomerContactRequest,
} from '@youkuai/contracts';

import { apiRequest } from './project-api.ts';

export async function listCustomers(token: string, search = '', afterCode?: string): Promise<CustomerListResponse> {
  const query = new URLSearchParams({ limit: '50' });
  if (search.trim()) query.set('search', search.trim());
  if (afterCode) query.set('afterCode', afterCode);
  return CustomerListResponseSchema.parse(await apiRequest(`/customers?${query}`, token));
}

export async function getCustomer(token: string, customerId: string): Promise<CustomerSummary> {
  return CustomerSummarySchema.parse(await apiRequest(`/customers/${encodeURIComponent(customerId)}`, token));
}

export async function listCustomerResponsibilityOptions(token: string): Promise<ProjectCreationOptionsResponse> {
  return ProjectCreationOptionsResponseSchema.parse(await apiRequest('/customers/responsibility-options?limit=100', token));
}

export async function createCustomer(token: string, input: {
  name: string; legalName: string | null; customerType: CustomerType;
  organization: string | null; department: string | null;
  status: 'PROSPECT' | 'ACTIVE' | 'INACTIVE'; region: string | null; address: string | null;
  notes: string | null; responsiblePersonIds: string[];
}, key: string): Promise<CustomerWriteResponse> {
  return CustomerWriteResponseSchema.parse(await apiRequest('/customers', token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
    body: JSON.stringify({ expectedVersion: 0, ...input, name: input.name.trim() }),
  }));
}

export async function updateCustomer(token: string, input: {
  customerId: string; expectedVersion: number; legalName: string | null; customerType: CustomerType;
  organization: string | null; department: string | null; name?: string | undefined;
  status: 'PROSPECT' | 'ACTIVE' | 'INACTIVE'; region: string | null; address: string | null;
  notes: string | null; responsiblePersonIds: string[]; key: string;
}): Promise<CustomerWriteResponse> {
  const { customerId, key, ...body } = input;
  return CustomerWriteResponseSchema.parse(await apiRequest(`/customers/${encodeURIComponent(customerId)}`, token, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
    body: JSON.stringify(body),
  }));
}

export async function createCustomerContact(token: string, customerId: string,
  input: CreateCustomerContactRequest, key: string): Promise<CustomerContactWriteResponse> {
  return CustomerContactWriteResponseSchema.parse(await apiRequest(`/customers/${encodeURIComponent(customerId)}/contacts`, token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: JSON.stringify(input),
  }));
}

export async function updateCustomerContact(token: string, customerId: string, contactId: string,
  input: UpdateCustomerContactRequest, key: string): Promise<CustomerContactWriteResponse> {
  return CustomerContactWriteResponseSchema.parse(await apiRequest(
    `/customers/${encodeURIComponent(customerId)}/contacts/${encodeURIComponent(contactId)}`, token, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: JSON.stringify(input),
    }));
}

export async function removeCustomerContact(token: string, customerId: string, contactId: string,
  expectedVersion: number, key: string): Promise<CustomerContactWriteResponse> {
  return CustomerContactWriteResponseSchema.parse(await apiRequest(
    `/customers/${encodeURIComponent(customerId)}/contacts/${encodeURIComponent(contactId)}/remove`, token, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
      body: JSON.stringify({ expectedVersion }),
    }));
}

export async function setPrimaryCustomerContact(token: string, customerId: string, contactId: string,
  expectedCustomerVersion: number, key: string): Promise<CustomerContactWriteResponse> {
  return CustomerContactWriteResponseSchema.parse(await apiRequest(`/customers/${encodeURIComponent(customerId)}/primary-contact`, token, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
    body: JSON.stringify({ expectedCustomerVersion, contactId }),
  }));
}

export async function saveCustomerBillingProfile(token: string, customerId: string,
  input: SaveCustomerBillingProfileRequest, key: string): Promise<CustomerBillingProfileWriteResponse> {
  return CustomerBillingProfileWriteResponseSchema.parse(await apiRequest(`/customers/${encodeURIComponent(customerId)}/billing-profile`, token, {
    method: 'PUT', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: JSON.stringify(input),
  }));
}
